/* eslint max-statements: ["error", {max: 25}] -- large amount of return values to set up */
/* eslint max-lines-per-function: ["error", {max: 180}] -- large amount of return values to set up */
import type { PropsWithChildren } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import isNil from "lodash/isNil";

import { IndividualSupportChannelPrefix } from "@/constants/GroupChannels";
import type { GroupChannel } from "@/crossPlatform/Sendbird";
import {
  ConnectionHandler,
  GroupChannelHandler,
  PushTriggerOption,
  useSendbirdChat,
} from "@/crossPlatform/Sendbird";
import useUnreadMessageCount from "@/hooks/useUnreadMessageCount";
import useAppMonitor from "@/services/AppMonitor";
import { useGetFullProfileQuery } from "@/store/api/user";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { groupedChatChannelsByGroupType } from "@/utils/groupChannel.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { getPreferredFirstName } from "@/utils/user.util";

interface UseChatChannelProps {
  admissionsChannels: GroupChannel[];
  groupChannels: GroupChannel[];
  individualChannels: GroupChannel[];
  individualSupportChannels: GroupChannel[];
  selectedPatientId?: string;
  selectedPatientName?: string;
  selectedPatientProfile?: FullProfileResponse | LinkedPatientProfile;
  setSelectedPatientId: (patientId: string) => void;
}

const ChannelUpdateHandler = "channelUpdateHandler";
const ChatChannelConnectionHandler = "chatChannelConnectionHandler";

const useChatChannels = (isConnected: boolean): UseChatChannelProps => {
  const { captureException } = useAppMonitor();
  const { sdk: sendbirdSdk } = useSendbirdChat();
  const { getUnreadMessageCountAsync } = useUnreadMessageCount(isConnected);
  const { externalId: appUserExternalId, isPatient: isAppUserPatient } =
    useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector();
  const { data: fullProfileData } = useGetFullProfileQuery();

  const [allChannels, setAllChannels] = useState<GroupChannel[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>();

  const updateChannelPushTriggerOption = useCallback(
    (channel: GroupChannel) => {
      if (channel.myPushTriggerOption === PushTriggerOption.OFF) return;
      channel.setMyPushTriggerOption(PushTriggerOption.OFF).catch((error) => {
        captureException(error);
      });
    },
    [captureException],
  );

  const isLoadingChannelsRef = useRef(false);

  const loadChannels = useCallback(() => {
    if (!isConnected || isLoadingChannelsRef.current) {
      return;
    }

    isLoadingChannelsRef.current = true;

    // MyGroupChannelListQuery is a one-shot cursor — reusing it after .next() returns empty.
    const query = sendbirdSdk.groupChannel.createMyGroupChannelListQuery({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      includeEmpty: true,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      includeMetaData: true,
      limit: 100,
    });

    query
      .next()
      .then((channels) => {
        channels.forEach(updateChannelPushTriggerOption);
        setAllChannels(channels);
      })
      .catch((error) => {
        captureException(error);
      })
      .finally(() => {
        isLoadingChannelsRef.current = false;
      });
  }, [
    isConnected,
    sendbirdSdk,
    updateChannelPushTriggerOption,
    captureException,
    setAllChannels,
  ]);

  const updateChatState = useCallback(() => {
    loadChannels();
    void getUnreadMessageCountAsync();
  }, [getUnreadMessageCountAsync, loadChannels]);

  const updateChatStateRef = useRef(updateChatState);
  useEffect(() => {
    updateChatStateRef.current = updateChatState;
  }, [updateChatState]);

  // Initialize Sendbird handlers only after connected (SDK is guaranteed stable)
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const connectionHandler = new ConnectionHandler({
      onConnected: () => updateChatStateRef.current(),
      onReconnectSucceeded: () => updateChatStateRef.current(),
    });

    sendbirdSdk.addConnectionHandler(
      ChatChannelConnectionHandler,
      connectionHandler,
    );

    const unreadHandler = new GroupChannelHandler({
      onChannelChanged: () => updateChatStateRef.current(),
      onChannelDeleted: () => updateChatStateRef.current(),
      onChannelFrozen: () => updateChatStateRef.current(),
      onChannelUnfrozen: () => updateChatStateRef.current(),
    });

    sendbirdSdk.groupChannel.addGroupChannelHandler(
      ChannelUpdateHandler,
      unreadHandler,
    );

    updateChatStateRef.current();

    return () => {
      sendbirdSdk.removeConnectionHandler(ChatChannelConnectionHandler);
      sendbirdSdk.groupChannel.removeGroupChannelHandler(ChannelUpdateHandler);
    };
  }, [isConnected, sendbirdSdk]);

  const appUserChannels = useMemo(() => {
    if (isAppUserPatient) {
      return groupedChatChannelsByGroupType(allChannels);
    }

    const patientChannels = allChannels.filter((channel: GroupChannel) => {
      const channelMetadata: ChannelMetadata = channel.cachedMetaData;
      return channelMetadata.patientId === selectedPatientId;
    });

    return groupedChatChannelsByGroupType(patientChannels);
  }, [allChannels, isAppUserPatient, selectedPatientId]);

  const individualSupportChannels = groupedChatChannelsByGroupType(
    allChannels,
  ).directMessageChannels.filter((channel) =>
    channel.url.startsWith(IndividualSupportChannelPrefix),
  );

  const handleSetSelectedPatientId = (patientId: string) => {
    setSelectedPatientId(patientId);
  };

  const selectedPatientProfile = useMemo(() => {
    if (
      !isNotNilNotEmpty(proxyPatients) ||
      !isNotNilNotEmpty(selectedPatientId)
    )
      return undefined;
    return proxyPatients.find(
      (patient) => patient.externalId === selectedPatientId,
    );
  }, [proxyPatients, selectedPatientId]);

  const selectedPatientName = useMemo(() => {
    if (isNil(selectedPatientProfile)) return undefined;
    return getPreferredFirstName({
      chosenName: selectedPatientProfile.chosenName,
      firstName: selectedPatientProfile.firstName,
    });
  }, [selectedPatientProfile]);
  return {
    admissionsChannels: appUserChannels.admissionsChannels,
    groupChannels: appUserChannels.groupChannels,
    individualChannels: appUserChannels.directMessageChannels,
    individualSupportChannels,
    selectedPatientId: isAppUserPatient ? appUserExternalId : selectedPatientId,
    selectedPatientName,
    selectedPatientProfile: isAppUserPatient
      ? fullProfileData
      : selectedPatientProfile,
    setSelectedPatientId: handleSetSelectedPatientId,
  };
};

const ChatChannelContext = createContext<undefined | UseChatChannelProps>(
  undefined,
);

interface ChatChannelContextProviderProps extends PropsWithChildren {
  isConnected: boolean;
}

const ChatChannelContextProvider = ({
  children,
  isConnected,
}: ChatChannelContextProviderProps) => {
  const value = useChatChannels(isConnected);

  return (
    <ChatChannelContext.Provider value={value}>
      {children}
    </ChatChannelContext.Provider>
  );
};

const useChatChannelContext = (): UseChatChannelProps => {
  const context = useContext(ChatChannelContext);

  if (context === undefined) {
    throw new Error(
      "useChatChannelContext must be used within a ChatChannelContextProvider",
    );
  }

  return context;
};

export { ChatChannelContextProvider, useChatChannels };
export type { UseChatChannelProps };
export default useChatChannelContext;
